import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
} from "@dexteel/mesf-core";
import { Button, Grid2, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { OperatorSelector } from "../selectors/OperatorSelector";
import { CommentsField } from "./CommentsField";
import { updateCarrierStatus } from "@/pages/mill/carriers/repositories/CarrierAssemblyRepository";

export const SignOffModal = ({
	assetId,
	open,
	onHide,
	partId,
	info = { partName: "", sectionName: "", standName: "" },
}: {
	assetId?: number;
	open: boolean;
	onHide: (success: boolean) => void;
	partId: number;
	info: {
		partName?: string | null;
		sectionName?: string | null;
		standName?: string | null;
	};
}) => {
	const [comments, setComments] = useState("");
	const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(
		null,
	);
	const [error, setError] = useState("");

	const queryClient = useQueryClient();
	const signOff = useMutation(
		() => {
			return updateCarrierStatus({
				partId: partId,
				newStatus: "S",
				operatorId: selectedOperatorId,
				comments: "",
			});
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: ["carrierForm"] });
				await queryClient.invalidateQueries({ queryKey: ["carriersAssembly"] });
				onClose();
				onHide(true);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	const onClose = () => {
		setSelectedOperatorId(null);
		setComments("");
	};

	return (
		<>
			{open && <HelmetDexteel title={`${info.partName} - Confirm Sign Off`} />}
			<MesfModal
				title="CONFIRM SIGN OFF"
				open={open}
				id="confirm-sign-off-modal"
				maxWidth="sm"
				handleClose={() => onHide(false)}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<p>
								{`Are you sure that you want to do the sign off for the carrier: `}
								<strong>{info.partName}</strong>
								{`, section: `}
								<strong>{info.sectionName}</strong>
								{` and stand: `}
								<strong>{info.sectionName}</strong>
							</p>
						</Grid2>
						<Grid2 container size={{ xs: 12, md: 12 }} spacing={1}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<OperatorSelector
									assetId={assetId}
									value={selectedOperatorId}
									onChange={(value: number) => setSelectedOperatorId(value)}
									label="Operator"
								/>
							</Grid2>
							{!selectedOperatorId && (
								<Grid2 size={{ md: 12, xs: 12 }}>
									<Typography variant="subtitle2" color="textSecondary">
										Select an operator
									</Typography>
								</Grid2>
							)}
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<CommentsField
								value={comments}
								onChange={(comment) => setComments(comment)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => {
									onClose();
									onHide(false);
								}}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<ButtonWithLoading
								fullWidth
								onClick={() => signOff.mutate()}
								variant="contained"
								color="primary"
								disabled={!selectedOperatorId}
								isLoading={signOff.isPending}
								data-test-id="confirm-sign-off-button"
							>
								Yes
							</ButtonWithLoading>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
